import React from 'react';
import { Input } from '../../../shared/components/Input';

/**
 * Slide-over drawer for the project-level platform rates. Mirrors the Material
 * Traceability cost-assumptions drawer: every rate is prefilled from the active
 * configuration and overridable for this proposal. Overrides are stored in the
 * backend-native units (the margin as a fraction); the drawer converts only for
 * display.
 */

const PERCENT = 100;

// Fallback rates, used only when the active config has not loaded yet.
export const PLATFORM_RATE_FALLBACKS: Record<string, number> = {
  baseInfraMonthly: 500,
  perModuleInfraMonthly: 60,
  baseSupportHours: 40,
  supportHourlyRate: 40,
  perUserMonthly: 40,
  marginTarget: 0.75,
};

export const RETENTION_RATE_FALLBACKS: Record<string, number> = {
  coldArchiveAnnualFee: 3000,
  warmAccessAnnualFee: 15000,
};

interface RateField {
  key: string;
  label: string;
  percent?: boolean;
  step?: number;
  tooltip: string;
}

const RATE_FIELDS: RateField[] = [
  { key: 'baseInfraMonthly', label: 'Base infra $/mo', tooltip: 'Shared baseline infra cost per month, for any deal.' },
  { key: 'perModuleInfraMonthly', label: 'Per-module infra $/mo', tooltip: 'Infra increment added per selected module. Total infra = baseline + this x module count.' },
  { key: 'baseSupportHours', label: 'Support hours / mo', tooltip: 'Support hours included per month.' },
  { key: 'supportHourlyRate', label: 'Support $/hr', tooltip: 'Hourly rate for the support hours.' },
  { key: 'perUserMonthly', label: 'Per-user $/mo', tooltip: 'Platform fee charged per user per month.' },
  { key: 'marginTarget', label: 'Margin %', percent: true, step: 1, tooltip: 'Target margin: price = cost / (1 - margin).' },
];

const RETENTION_FIELDS: RateField[] = [
  { key: 'coldArchiveAnnualFee', label: 'Cold archive $/yr', tooltip: 'Annual fee for the cold-archive retention tier.' },
  { key: 'warmAccessAnnualFee', label: 'Warm access $/yr', tooltip: 'Annual fee for the warm-access retention tier.' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  config?: Record<string, number>;
  overrides: Record<string, number>;
  onOverrideChange: (key: string, value: number | undefined) => void;
  retentionConfig?: Record<string, number>;
  retentionOverrides?: Record<string, number>;
  onRetentionOverrideChange?: (key: string, value: number | undefined) => void;
  onResetAll: () => void;
}

// One editable rate field with its default hint and reset control. Percent
// fields display x100 and store the fraction.
const RateInput: React.FC<{
  field: RateField;
  defaultValue: number;
  override: number | undefined;
  onChange: (value: number | undefined) => void;
}> = ({ field, defaultValue, override, onChange }) => {
  const isOverridden = override !== undefined;
  const native = isOverridden ? override : defaultValue;
  const shown = field.percent ? native * PERCENT : native;
  return (
    <div>
      <Input
        id={`platform-rate-${field.key}`}
        label={field.label}
        tooltip={field.tooltip}
        type="number"
        step={field.step ?? 'any'}
        value={shown}
        className={isOverridden ? 'border-primary' : ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(undefined);
            return;
          }
          const n = Number(raw);
          if (Number.isNaN(n)) return;
          onChange(field.percent ? n / PERCENT : n);
        }}
      />
      <div className="mt-0.5 flex items-center justify-between text-[11px] text-heritage-grey">
        <span>default {field.percent ? `${defaultValue * PERCENT}%` : defaultValue}</span>
        {isOverridden && (
          <button type="button" className="text-primary hover:underline" onClick={() => onChange(undefined)}>
            reset
          </button>
        )}
      </div>
    </div>
  );
};

export const PlatformRatesDrawer: React.FC<Props> = ({
  open,
  onClose,
  config,
  overrides,
  onOverrideChange,
  retentionConfig,
  retentionOverrides,
  onRetentionOverrideChange,
  onResetAll,
}) => {
  if (!open) return null;

  const defaultValue = (key: string): number =>
    config?.[key] ?? PLATFORM_RATE_FALLBACKS[key] ?? 0;
  const retentionOverridesObj = retentionOverrides ?? {};
  const retentionDefault = (key: string): number =>
    retentionConfig?.[key] ?? RETENTION_RATE_FALLBACKS[key] ?? 0;

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Platform rates">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-[#E2E5EA] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold font-heading text-blueprint-blue">Platform rates</h3>
            <p className="mt-0.5 text-xs text-heritage-grey">
              From the active configuration. Edit any value to override it for this proposal only.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-heritage-grey hover:text-blueprint-blue" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          <div>
            <h4 className="text-sm font-medium text-blueprint-blue">Platform fee</h4>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
              {RATE_FIELDS.map((field) => (
                <RateInput
                  key={field.key}
                  field={field}
                  defaultValue={defaultValue(field.key)}
                  override={overrides[field.key]}
                  onChange={(value) => onOverrideChange(field.key, value)}
                />
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-blueprint-blue">Retention annual fees</h4>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
              {RETENTION_FIELDS.map((field) => (
                <RateInput
                  key={field.key}
                  field={field}
                  defaultValue={retentionDefault(field.key)}
                  override={retentionOverridesObj[field.key]}
                  onChange={(value) => onRetentionOverrideChange?.(field.key, value)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-[#E2E5EA] px-5 py-3">
          <button type="button" onClick={onResetAll} className="text-sm text-text-grey hover:text-blueprint-blue">
            Reset all to defaults
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
